import { isDefined, isNotDefined } from '@sgme/fp';

import type { BlotterTradeEntry } from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import { canReceiveFillReport, fillReportReceived, getExecution, GrowlSlice } from '@/store/growl/slice.ts';
import type { Growl } from '@/store/growl/state/index.ts';
import { notifyGrowlClosed } from '@/store/growl/whenGrowlClosed.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';

const RECOVERABLE_GROWL_TYPES: Growl['type'][] = ['swapDealExecution', 'cashDealExecution'];

/**
 * Keeps the growl of a deal consistent with what the blotter says about it.
 *
 * The blotter pushes a live trade notification whose `electronicReference` is the multipass id —
 * which is also the growl id — and whose `id` is the trade id: either the real one, or the
 * `NOT_YET_INTEGRATED` marker while the back office has not integrated the deal. Both are worth
 * relaying: the growl and the blotter must not tell two different stories about the same deal.
 *
 * This is the only guaranteed path. Multipass does not always send a fill report, and never sends
 * a corrective one, so an execution that only got its deal reply would otherwise promise a
 * confirmation that never comes.
 *
 * The raw notification is read from the action rather than from the blotter state, because
 * `electronicReference` is dropped by the entry conversion and the entry itself may be discarded
 * by the blotter date filter.
 */
export function registerGrowlListeners(startAppListening: StartAppListening): void {
  // releases the `ReceiveFillReport` listener an execution kept alive while its growl was open
  startAppListening({
    actionCreator: GrowlSlice.actions.delete,
    effect: ({ payload: { growlId } }) => {
      notifyGrowlClosed(growlId);
    },
  });

  startAppListening({
    actionCreator: blotterSlice.actions.liveTradeReceived,
    effect: ({ payload: trades }, { dispatch, getState }) => {
      const state = getState();

      for (const [growlId, tradeIds] of groupTradeIdsByGrowlId(trades)) {
        const growl = getExecution(state, growlId);

        // the reducer stays the authority on which transitions are legal: filtering here only
        // avoids dispatching an action that it would discard
        if (
          isNotDefined(growl) ||
          !RECOVERABLE_GROWL_TYPES.includes(growl.type) ||
          !('status' in growl.data) ||
          !canReceiveFillReport(growl.data.status)
        ) {
          continue;
        }

        dispatch(fillReportReceived({ growlId, tradeIds }));
      }
    },
  });
}

function groupTradeIdsByGrowlId(trades: BlotterTradeEntry[]): Map<string, string[]> {
  const tradeIdsByGrowlId = new Map<string, string[]>();

  for (const trade of trades) {
    const growlId = trade.electronicReference;

    if (isNotDefined(growlId) || growlId === '' || isNotDefined(trade.id)) {
      continue;
    }

    const tradeIds = tradeIdsByGrowlId.get(growlId);

    if (isDefined(tradeIds)) {
      tradeIds.push(trade.id);
      continue;
    }

    tradeIdsByGrowlId.set(growlId, [trade.id]);
  }

  return tradeIdsByGrowlId;
}
